<template>
  <!-- SEÇÃO: SOBRE O EVENTO -->
  <section class="about-content" id="evento">
    <div class="about-middle w-100 px-4 m-auto gap-x-3">
      <div class="event-infos md:w-[100%] mt-1">
        
        <!-- Descrição do Evento -->
        <div v-if="settings?.description" class="about-area">
          <div class="minimize-overview">
            <div class="text-about minimize-overview-content">
              <div class="content-description minimize-overview-text" v-html="settings.description">
              </div>
            </div>
          </div>
        </div>

        <!-- GALERIA DO LOCAL COM CARROSSEL -->
        <div v-if="photos && photos.length > 0" class="event-local mt-4">
          <div class="carousel-container relative mb-8">
            <div class="carousel-wrapper overflow-hidden rounded-lg">
              <div class="carousel-track flex transition-transform duration-300 ease-in-out"
                   :style="{ transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)` }">
                <div v-for="(image, index) in photos"
                     :key="index"
                     class="carousel-slide flex-shrink-0"
                     :class="slidesToShow === 2 ? 'w-1/2' : slidesToShow === 4 ? 'w-1/4' : 'w-1/3'">
                  <div class="px-2">
                    <img :src="image.photo"
                         :alt="image.title || 'Foto do local'"
                         class="aspect-[4/3] w-full object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                         @click="openImageModal(image)" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Botões de navegação (ocultos no desktop se tiver 4 ou menos imagens) -->
            <button
              v-if="showNavigationArrows"
              @click="prevSlide"
              class="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200 z-10"
              :disabled="currentSlide === 0"
              :class="{ 'opacity-50 cursor-not-allowed': currentSlide === 0 }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>

            <button
              v-if="showNavigationArrows"
              @click="nextSlide"
              class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200 z-10"
              :disabled="currentSlide >= maxSlide"
              :class="{ 'opacity-50 cursor-not-allowed': currentSlide >= maxSlide }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Endereço e Mapa (independente da galeria de fotos) -->
        <div v-if="showAddress || showMap" class="mt-4" id="local">
          <div v-if="showAddress">
            <span class="text-date font-semibold text-[12px] md:text-sm">Endereço: </span>
            <span class="text-date font-semibold text-sky-500">{{ displayAddress }}</span>
          </div>

          <!-- Mapa - só mostra se NÃO for undefined e tiver URL -->
          <iframe v-if="showMap"
                  :src="settings.map_embed_url"
                  width="100%"
                  height="450"
                  style="border:0;"
                  allowfullscreen=""
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                  class="mt-4 rounded-lg">
          </iframe>
        </div>
      </div>
    </div>

    <!-- MODAL DE IMAGEM -->
    <div v-if="showImageModal" 
         id="image-modal"
         class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm" 
         @click="closeImageModal">
      <div class="relative max-w-5xl w-full" @click.stop>
        <!-- Botão fechar -->
        <button @click="closeImageModal" 
                class="absolute top-4 right-4 z-10 bg-white hover:bg-gray-100 rounded-full p-2 transition shadow-lg">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <!-- Imagem -->
        <img :src="selectedImage.photo" 
             :alt="selectedImage.title || 'Foto do local'" 
             class="w-full h-auto rounded-lg shadow-2xl">

        <!-- Título e descrição (se existirem) -->
        <div v-if="selectedImage.title || selectedImage.description" 
             class="bg-white rounded-b-lg p-4 mt-2">
          <h3 v-if="selectedImage.title" class="text-lg font-semibold text-gray-900 mb-1">
            {{ selectedImage.title }}
          </h3>
          <p v-if="selectedImage.description" class="text-sm text-gray-600">
            {{ selectedImage.description }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useLandingPage } from '@/composables/useLandingPage'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

export default {
  name: 'AboutEventSection',
  setup() {
    const { settings, photos } = useLandingPage()
    const currentSlide = ref(0)
    const slidesToShow = ref(4)
    const showImageModal = ref(false)
    const selectedImage = ref({})

    // ✅ NOVO: Verificar se deve mostrar endereço
    const showAddress = computed(() => {
      // Não mostra se address_undefined for true
      if (settings.value?.address_undefined) return false
      // Mostra se tiver address OU address_complete
      return !!(settings.value?.address || settings.value?.address_complete)
    })

    // ✅ NOVO: Texto do endereço ou "A definir"
    const displayAddress = computed(() => {
      if (settings.value?.address_undefined) return 'A definir'
      return settings.value?.address || settings.value?.address_complete || 'A definir'
    })

    // ✅ NOVO: Verificar se deve mostrar mapa
    const showMap = computed(() => {
      // Não mostra se address_undefined for true
      if (settings.value?.address_undefined) return false
      // Só mostra se tiver map_embed_url
      return !!(settings.value?.map_embed_url)
    })

    // Calcular máximo de slides
    const maxSlide = computed(() => {
      if (!photos.value) return 0
      return Math.max(0, photos.value.length - slidesToShow.value)
    })

    // Mostrar setas de navegação apenas se necessário
    const showNavigationArrows = computed(() => {
      if (!photos.value) return false
      // Mobile: sempre mostrar se houver mais de 2 fotos
      if (slidesToShow.value === 2) return photos.value.length > 2
      // Desktop: mostrar se houver mais de 4 fotos
      return photos.value.length > 4
    })

    // Atualizar slides responsivos
    const updateSlidesToShow = () => {
      if (typeof window !== 'undefined') {
        slidesToShow.value = window.innerWidth < 768 ? 2 : 4
      }
    }

    // Navegação
    const nextSlide = () => {
      if (currentSlide.value < maxSlide.value) currentSlide.value++
    }

    const prevSlide = () => {
      if (currentSlide.value > 0) currentSlide.value--
    }

    // Modal
    const openImageModal = (image) => {
      selectedImage.value = image
      showImageModal.value = true
      document.body.style.overflow = 'hidden'
    }

    const closeImageModal = () => {
      showImageModal.value = false
      selectedImage.value = {}
      document.body.style.overflow = 'auto'
    }

    const handleResize = () => {
      updateSlidesToShow()
      currentSlide.value = Math.min(currentSlide.value, maxSlide.value)
    }

    onMounted(() => {
      updateSlidesToShow()
      window.addEventListener('resize', handleResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      settings,
      photos,
      currentSlide,
      slidesToShow,
      maxSlide,
      showNavigationArrows,
      showImageModal,
      selectedImage,
      showAddress,
      displayAddress,
      showMap,
      nextSlide,
      prevSlide,
      openImageModal,
      closeImageModal
    }
  }
}
</script>

<style scoped>
#image-modal {
  backdrop-filter: blur(4px);
}
</style>
